import React, { useState, useEffect } from 'react';
import { Layout, Card, Typography, Alert, Space, notification } from 'antd';
import { ApiKeyInput } from '../../components/index.js';
import { testHubspotConnection } from '../../services/index.js';
import { validateHubSpotToken } from '../../utils/index.js';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const SettingsPage = () => {
  const [hubspotToken, setHubspotToken] = useState('');
  const [loading, setLoading] = useState(false);

  // Load saved token on component mount
  useEffect(() => {
    const savedToken = localStorage.getItem('hubspot_api_token');
    if (savedToken) {
      setHubspotToken(savedToken);
    }
  }, []);

  const handleTokenChange = (value) => {
    setHubspotToken(value);
  };

  const handleTestConnection = async (token) => {
    setLoading(true);
    try {
      const result = await testHubspotConnection(token);
      return result;
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToken = async (token) => {
    try {
      // Validate token format first
      if (!validateHubSpotToken(token)) {
        throw new Error('Invalid token format. Please check your HubSpot API token.');
      }

      // Test the connection before saving
      const testResult = await handleTestConnection(token);
      
      if (!testResult.success) {
        throw new Error(testResult.error || 'Failed to connect to HubSpot API');
      }

      // Save to localStorage
      localStorage.setItem('hubspot_api_token', token);
      
      notification.success({
        message: 'API Token Saved',
        description: `HubSpot API token saved successfully. Found ${testResult.dealCount || 0} deals.`,
        placement: 'topRight'
      });

    } catch (error) {
      notification.error({
        message: 'Save Failed',
        description: error.message,
        placement: 'topRight'
      });
      throw error; // Re-throw so the component can handle it
    }
  };

  const validateToken = (token) => {
    if (!token || token.length < 20) {
      return {
        valid: false,
        message: 'Token must be at least 20 characters long'
      };
    }

    if (!validateHubSpotToken(token)) {
      return {
        valid: false,
        message: 'Invalid token format. Should contain only alphanumeric characters, hyphens, and underscores.'
      };
    }

    return { valid: true };
  };

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          {/* HubSpot API Configuration */}
          <Card 
            title="HubSpot API Configuration" 
            style={{ marginBottom: 24 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Alert
                message="HubSpot API Token Required"
                description={
                  <div>
                    <p>To import deal data from HubSpot, you need to provide a valid API token.</p>
                    <p><strong>How to get your HubSpot API token:</strong></p>
                    <ol>
                      <li>Go to your HubSpot account settings</li>
                      <li>Navigate to Integrations → API key</li>
                      <li>Copy your API key</li>
                      <li>Paste it in the field below</li>
                    </ol>
                    <p><em>Note: The token will be stored securely in your browser&apos;s local storage.</em></p>
                  </div>
                }
                type="info"
                showIcon
              />

              <ApiKeyInput
                label="HubSpot API Token"
                placeholder="Enter your HubSpot API token..."
                value={hubspotToken}
                onChange={handleTokenChange}
                onTest={handleTestConnection}
                onSave={handleSaveToken}
                validator={validateToken}
                helperText="Your API token is stored locally and never transmitted except to HubSpot's API servers."
                disabled={loading}
              />
            </Space>
          </Card>

          {/* Application Information */}
          <Card title="Application Information">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Title level={5}>Commission Tracker v1.0</Title>
                <Paragraph type="secondary">
                  This application helps match WellSky client data with HubSpot deal data 
                  to generate commission reports for sales representatives.
                </Paragraph>
              </div>

              <div>
                <Title level={5}>Data Sources</Title>
                <ul>
                  <li><strong>WellSky:</strong> Excel file upload (manual)</li>
                  <li><strong>HubSpot:</strong> API integration (requires token)</li>
                </ul>
              </div>

              <div>
                <Title level={5}>Security</Title>
                <Paragraph type="secondary">
                  API tokens are stored locally in your browser and are only used to communicate 
                  with their respective services. No sensitive data is transmitted to external servers 
                  except for the official API endpoints of the integrated services.
                </Paragraph>
              </div>
            </Space>
          </Card>
        </div>
      </Content>
    </Layout>
  );
};

export default SettingsPage;
